/**
 * End of Period (EOP) → Report descriptor.
 *
 * Maps a saved `pnl_reports` row into the unified ReportDriver descriptor
 * (see components/reports/ReportDriver.jsx) so the report that's shown through
 * the approval/review flow can be viewed, printed, downloaded, and emailed
 * with the same branded chrome as every other Countertop report.
 *
 * The function is pure — it reads only the persisted snapshot on the row
 * (report_data category rows + the meta/unallocated pseudo-rows) plus a small
 * presentation context (store name, who generated it, the period dates). It
 * never re-derives figures from live counts, so a historical report always
 * renders exactly as it was saved.
 */

import { valuationRuleText } from './countItemReporting';

const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Always shows a dollar amount (0 → $0.00) — used in the inventory/COGS bridge
// and footer totals where a blank cell would break the columnar read.
const money = (v) => CURRENCY.format(num(v));

// Shows a dollar amount, or an em-dash when the value is genuinely absent
// (null/undefined/blank) — used for optional sales fields that were never
// tracked or provided for the period.
const moneyOrDash = (v) =>
  (v == null || v === '' || !Number.isFinite(Number(v)) ? '—' : CURRENCY.format(Number(v)));

const pctOrDash = (v) =>
  (v == null || !Number.isFinite(Number(v)) ? '—' : `${Number(v)}%`);

// ── report_data row classifiers (mirror EOPWizard) ────────────────────────────
const isCategoryRow = (r) => r && r.pnl_category_id != null && !r._row_type;
const findMetaRow = (rows) =>
  (Array.isArray(rows) ? rows : []).find((r) => r && r._row_type === 'meta') || null;
const findUnallocatedRow = (rows) =>
  (Array.isArray(rows) ? rows : []).find((r) => r && r._row_type === 'unallocated') || null;

export function isLegacyEopReport(report) {
  if (!report) return false;
  const rows = Array.isArray(report.report_data) ? report.report_data : [];
  if (rows.some((r) => r && Number(r._v) >= 2)) return false;
  if (report.report_type && report.report_type !== 'usage_only') return true;
  return rows.some((r) => r && (
    r.sales != null || r.discounts != null || r.comps != null
    || r.recipe_cost_pct != null || r.net_sales != null || r.cogs_percent != null
  ));
}

/**
 * @param {object} row  A pnl_reports record (with report_data array).
 * @param {object} ctx
 * @param {string} ctx.storeName        Restaurant name (hero header).
 * @param {string} [ctx.createdBy]      Display name of the logged-in author.
 * @param {string} [ctx.periodStartLabel] Pre-formatted beginning-count date.
 * @param {string} [ctx.periodEndLabel]   Pre-formatted ending-count date.
 * @param {string[]} [ctx.counters]     Names of who completed the counts.
 * @param {string} [ctx.generatedAt]    ISO timestamp the report was generated.
 * @returns {object} ReportDriver descriptor.
 */
export function buildEopReportDescriptor(row, ctx = {}) {
  const {
    storeName = 'Your Restaurant',
    createdBy = '',
    periodStartLabel = '',
    periodEndLabel = '',
    counters = [],
    generatedAt = new Date().toISOString(),
  } = ctx;

  const allRows = Array.isArray(row?.report_data) ? row.report_data : [];
  const catRows = allRows.filter(isCategoryRow);
  const metaRow = findMetaRow(allRows);
  const unallocRow = findUnallocatedRow(allRows);
  const legacy = isLegacyEopReport(row);
  const totalsMeta = metaRow?.totals || {};

  const sum = (field) => catRows.reduce((s, r) => s + num(r[field]), 0);

  // ── Headline figures ───────────────────────────────────────────────────────
  const beginning = sum('beginning_inventory');
  const ending = sum('ending_inventory');
  const purchases = sum('purchases');
  const transfersIn = sum('transfers_in');
  const transfersOut = sum('transfers_out');
  const otherAdj = sum('other_adjustments');
  const actualCogs = Number.isFinite(Number(row?.actual_usage_total))
    ? Number(row.actual_usage_total)
    : sum('cost_of_goods_used');
  const discounts = sum('discounts');
  const comps = sum('comps');
  const refunds = sum('refunds');
  const waste = sum('waste');

  const grossSales = totalsMeta.gross_sales != null
    ? totalsMeta.gross_sales
    : (catRows.some((r) => r.gross_sales != null) ? sum('gross_sales') : null);
  const netSales = row?.net_sales_total != null
    ? row.net_sales_total
    : (totalsMeta.net_sales != null ? totalsMeta.net_sales : null);
  const cogsPct = row?.aggregate_cogs_pct != null
    ? row.aggregate_cogs_pct
    : (totalsMeta.actual_cogs_pct != null ? totalsMeta.actual_cogs_pct : null);
  const adjustedCostPct = totalsMeta.adjusted_cost_pct ?? null;
  const impliedCostRate = totalsMeta.implied_cost_rate ?? null;
  const estDiscountCost = totalsMeta.est_discount_cost ?? null;
  const estCompCost = totalsMeta.est_comp_cost ?? null;
  const cogsPaidSales = totalsMeta.cogs_paid_sales ?? null;
  const transfersAndAdj = transfersIn - transfersOut + otherAdj;

  // ── Summary stat cards ──────────────────────────────────────────────────────
  const stats = [
    { label: 'Beginning Inventory', value: money(beginning) },
    { label: 'Purchases', value: money(purchases) },
    ...(transfersAndAdj !== 0 ? [{ label: 'Transfers & Adjustments', value: money(transfersAndAdj) }] : []),
    { label: 'Ending Inventory', value: money(ending) },
    { label: 'Actual COGS', value: money(actualCogs) },
    ...(netSales != null ? [{ label: 'Net Sales', value: money(netSales) }] : []),
    ...(cogsPct != null ? [{ label: 'Actual COGS %', value: `${cogsPct}%` }] : []),
    ...(adjustedCostPct != null ? [{ label: 'Adjusted Cost %', value: `${adjustedCostPct}%` }] : []),
    ...(waste > 0 ? [{ label: 'Waste (separate)', value: money(waste) }] : []),
  ];

  // ── Section 1: Inventory & Cost of Goods Used ──────────────────────────────
  const showTransferCols = !legacy;
  const invCols = [
    { key: 'category', label: 'P&L Category', align: 'text', width: '20%' },
    { key: 'beginning', label: 'Beginning Inv.', align: 'number' },
    { key: 'purchases', label: 'Purchases', align: 'number' },
    ...(showTransferCols ? [
      { key: 'transfersIn', label: 'Transfers In', align: 'number' },
      { key: 'transfersOut', label: 'Transfers Out', align: 'number' },
      { key: 'otherAdj', label: 'Other Adj.', align: 'number' },
    ] : []),
    { key: 'ending', label: 'Ending Inv.', align: 'number' },
    { key: 'cogs', label: 'Actual COGS', align: 'number' },
  ];
  const invRows = catRows.map((r) => ({
    category: r.pnl_category_name || 'Uncategorized',
    beginning: money(r.beginning_inventory),
    purchases: money(r.purchases),
    transfersIn: money(r.transfers_in),
    transfersOut: money(r.transfers_out),
    otherAdj: money(r.other_adjustments),
    ending: money(r.ending_inventory),
    cogs: money(r.cost_of_goods_used),
  }));
  const invTotals = {
    category: 'Total',
    beginning: money(beginning),
    purchases: money(purchases),
    transfersIn: money(transfersIn),
    transfersOut: money(transfersOut),
    otherAdj: money(otherAdj),
    ending: money(ending),
    cogs: money(actualCogs),
  };

  const sections = [{
    title: 'Inventory & Cost of Goods Used',
    sectionNote: showTransferCols
      ? 'Actual COGS = Beginning Inventory + Purchases + Transfers In − Transfers Out ± Other Adjustments − Ending Inventory'
      : 'Actual COGS = Beginning Inventory + Purchases − Ending Inventory',
    columns: invCols,
    rows: invRows,
    totals: invTotals,
  }];

  // ── Section 2: Sales, Discounts & Comps ────────────────────────────────────
  const hasSalesData = catRows.some((r) => (
    r.gross_sales != null || r.net_sales != null
    || r.discounts != null || r.comps != null || (legacy && r.sales != null)
  ));
  if (hasSalesData) {
    if (legacy) {
      sections.push({
        title: 'Sales by Category',
        columns: [
          { key: 'category', label: 'P&L Category', align: 'text', width: '28%' },
          { key: 'sales', label: 'Sales', align: 'number' },
          { key: 'discounts', label: 'Discounts', align: 'number' },
          { key: 'comps', label: 'Comps', align: 'number' },
          { key: 'netSales', label: 'Net Sales', align: 'number' },
          { key: 'cogsPct', label: 'COGS %', align: 'number' },
        ],
        rows: catRows.map((r) => ({
          category: r.pnl_category_name || 'Uncategorized',
          sales: moneyOrDash(r.sales),
          discounts: moneyOrDash(r.discounts),
          comps: moneyOrDash(r.comps),
          netSales: moneyOrDash(r.net_sales),
          cogsPct: pctOrDash(r.cogs_percent),
        })),
        totals: {
          category: 'Total',
          sales: moneyOrDash(catRows.some((r) => r.sales != null) ? sum('sales') : null),
          discounts: moneyOrDash(catRows.some((r) => r.discounts != null) ? discounts : null),
          comps: moneyOrDash(catRows.some((r) => r.comps != null) ? comps : null),
          netSales: moneyOrDash(netSales),
          cogsPct: pctOrDash(cogsPct),
        },
      });
    } else {
      sections.push({
        title: 'Sales, Discounts & Comps',
        sectionNote: 'Net Sales = Gross Sales − Discounts − Comps − Refunds (all at sales value)',
        columns: [
          { key: 'category', label: 'P&L Category', align: 'text', width: '20%' },
          { key: 'gross', label: 'Gross Sales', align: 'number' },
          { key: 'discounts', label: 'Discounts', align: 'number' },
          { key: 'comps', label: 'Comps', align: 'number' },
          { key: 'refunds', label: 'Refunds', align: 'number' },
          { key: 'netSales', label: 'Net Sales', align: 'number' },
          { key: 'cogsPct', label: 'Actual COGS %', align: 'number' },
        ],
        rows: catRows.map((r) => ({
          category: r.pnl_category_name || 'Uncategorized',
          gross: moneyOrDash(r.gross_sales),
          discounts: moneyOrDash(r.discounts),
          comps: moneyOrDash(r.comps),
          refunds: moneyOrDash(r.refunds),
          netSales: moneyOrDash(r.net_sales),
          cogsPct: pctOrDash(r.cogs_percent),
        })),
        totals: {
          category: 'Total',
          gross: moneyOrDash(grossSales),
          discounts: moneyOrDash(catRows.some((r) => r.discounts != null) ? discounts : null),
          comps: moneyOrDash(catRows.some((r) => r.comps != null) ? comps : null),
          refunds: moneyOrDash(catRows.some((r) => r.refunds != null) ? refunds : null),
          netSales: moneyOrDash(netSales),
          cogsPct: pctOrDash(cogsPct),
        },
      });
    }
  }

  // ── Section 3: Cost Allocation — Discounts & Comps ─────────────────────────
  // The estimated inventory cost carried by discounts and comps. These are
  // allocations of Actual COGS; they don't change the inventory-based COGS.
  const hasAllocation = !legacy && catRows.some((r) => (
    r.est_discount_cost != null || r.est_comp_cost != null || r.cogs_paid_sales != null
  ));
  if (hasAllocation) {
    sections.push({
      title: 'Cost Allocation — Discounts & Comps',
      sectionNote: 'Estimated inventory cost of discounts/comps = amount at sales value × implied actual cost rate.',
      columns: [
        { key: 'category', label: 'P&L Category', align: 'text', width: '20%' },
        { key: 'impliedRate', label: 'Implied Cost Rate', align: 'number' },
        { key: 'estDiscount', label: 'Est. Cost of Discounts', align: 'number' },
        { key: 'estComp', label: 'Est. Cost of Comps', align: 'number' },
        { key: 'paidCogs', label: 'COGS → Paid Sales', align: 'number' },
        { key: 'adjPct', label: 'Adjusted Cost %', align: 'number' },
      ],
      rows: catRows.map((r) => ({
        category: r.pnl_category_name || 'Uncategorized',
        impliedRate: pctOrDash(r.implied_cost_rate),
        estDiscount: moneyOrDash(r.est_discount_cost),
        estComp: moneyOrDash(r.est_comp_cost),
        paidCogs: moneyOrDash(r.cogs_paid_sales),
        adjPct: pctOrDash(r.adjusted_cost_pct),
      })),
      totals: {
        category: 'Total',
        impliedRate: pctOrDash(impliedCostRate),
        estDiscount: moneyOrDash(estDiscountCost),
        estComp: moneyOrDash(estCompCost),
        paidCogs: moneyOrDash(cogsPaidSales),
        adjPct: pctOrDash(adjustedCostPct),
      },
    });
  }

  // ── Section 4: Inventory Adjustment Notes ──────────────────────────────────
  const adjustmentRows = catRows.filter((r) => (
    num(r.other_adjustments) !== 0 || num(r.transfers_in) !== 0
    || num(r.transfers_out) !== 0 || (r.adjustment_note && String(r.adjustment_note).trim())
  ));
  if (!legacy && adjustmentRows.length > 0) {
    sections.push({
      title: 'Inventory Adjustments & Transfers',
      columns: [
        { key: 'category', label: 'P&L Category', align: 'text', width: '22%' },
        { key: 'transfersIn', label: 'Transfers In', align: 'number' },
        { key: 'transfersOut', label: 'Transfers Out', align: 'number' },
        { key: 'otherAdj', label: 'Other Adjustment', align: 'number' },
        { key: 'note', label: 'Note', align: 'text', width: '34%' },
      ],
      rows: adjustmentRows.map((r) => ({
        category: r.pnl_category_name || 'Uncategorized',
        transfersIn: money(r.transfers_in),
        transfersOut: money(r.transfers_out),
        otherAdj: money(r.other_adjustments),
        note: (r.adjustment_note && String(r.adjustment_note).trim()) || '—',
      })),
    });
  }

  // ── Section 5: Unallocated Adjustments ─────────────────────────────────────
  if (unallocRow) {
    const fields = ['discounts', 'comps', 'refunds', 'waste'].filter((f) => num(unallocRow[f]) !== 0);
    if (fields.length > 0) {
      sections.push({
        title: 'Unallocated Adjustments',
        sectionNote: 'Discounts, comps, refunds, or waste that were not assigned to a specific P&L category.',
        columns: [
          { key: 'field', label: 'Type', align: 'text', width: '40%' },
          { key: 'amount', label: 'Amount (at sales value)', align: 'number' },
        ],
        rows: fields.map((f) => ({
          field: f.charAt(0).toUpperCase() + f.slice(1),
          amount: money(unallocRow[f]),
        })),
      });
    }
  }

  // ── Section 6: Sales Data Source (audit) ───────────────────────────────────
  const cloverAudit = row?.clover_sales_audit;
  const posImport = metaRow?.pos_import;
  if (cloverAudit || posImport) {
    const auditRows = [];
    if (cloverAudit) {
      auditRows.push({ field: 'Sales source', detail: 'Clover (synced)' });
      if (cloverAudit.order_count != null) auditRows.push({ field: 'Orders pulled', detail: `${cloverAudit.order_count}` });
      if (cloverAudit.net_sales_total != null) auditRows.push({ field: 'Clover net sales', detail: money(cloverAudit.net_sales_total) });
      if (cloverAudit.period_start && cloverAudit.period_end) {
        auditRows.push({ field: 'Pull window', detail: `${cloverAudit.period_start} → ${cloverAudit.period_end}` });
      }
      if (cloverAudit.pulled_at) auditRows.push({ field: 'Pulled at', detail: new Date(cloverAudit.pulled_at).toLocaleString() });
    }
    if (posImport) {
      auditRows.push({ field: 'Sales source', detail: `Imported POS report${posImport.pos_system ? ` (${posImport.pos_system})` : ''}` });
      if (posImport.file_name) auditRows.push({ field: 'POS report file', detail: posImport.file_name });
      if (posImport.period_start && posImport.period_end) {
        auditRows.push({ field: 'Report period', detail: `${posImport.period_start} → ${posImport.period_end}` });
      }
      if (posImport.applied_at) auditRows.push({ field: 'Imported at', detail: new Date(posImport.applied_at).toLocaleString() });
    }
    if (auditRows.length > 0) {
      sections.push({
        title: 'Sales Data Source',
        columns: [
          { key: 'field', label: 'Detail', align: 'text', width: '32%' },
          { key: 'detail', label: '', align: 'text' },
        ],
        rows: auditRows,
      });
    }
  }

  // ── Report metadata (title, who/when, status) ──────────────────────────────
  const periodRange = (periodStartLabel && periodEndLabel)
    ? `${periodStartLabel} → ${periodEndLabel}`
    : (periodEndLabel || periodStartLabel || '');
  const savedAt = row?.created_at ? new Date(row.created_at) : null;
  const savedLabel = savedAt && !Number.isNaN(savedAt.getTime())
    ? savedAt.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
    : null;

  const meta = [];
  if (periodRange) meta.push({ label: 'Period', value: periodRange });
  if (metaRow?.completeness_label) meta.push({ label: 'Report Status', value: metaRow.completeness_label });
  meta.push({
    label: 'Calculation Basis',
    value: legacy ? 'Inventory usage (legacy)' : 'Actual cost with discount & comp allocation',
  });
  meta.push({ label: 'Valuation', value: valuationRuleText() });
  if (savedLabel) meta.push({ label: 'Saved', value: savedLabel });

  const completenessIncomplete = metaRow?.completeness_status
    && metaRow.completeness_status !== 'complete';
  const alert = completenessIncomplete
    ? {
      variant: 'warning',
      text: `${metaRow.completeness_label || 'Report is incomplete'}${
        (metaRow.completeness_notes || []).length > 0 ? ` — ${metaRow.completeness_notes.join('; ')}` : ''
      }`,
    }
    : null;

  return {
    store: { name: storeName },
    report: {
      title: row?.period_label || `End of Period — ${periodEndLabel || ''}`.trim(),
      subtitle: `End of Period P&L${periodRange ? ` · ${periodRange}` : ''}`,
      counters: counters.filter(Boolean),
      createdBy,
      generatedAt,
      meta,
      ...(alert ? { alert } : {}),
      footer: {
        note: 'Generated by Countertop App',
        url: 'www.CountertopApp.com',
      },
    },
    stats,
    sections,
  };
}

export default buildEopReportDescriptor;
